// ./src/components/generic/MediaSelector/validateMediaUrl.ts
//
// Validate that a pasted URL actually resolves to the media kind the caller
// asked for, before we let it be selected. Runs entirely in the browser (no
// server round-trip): images decode via <img>, direct video/audio files probe
// their metadata via a media element, and known embed providers (YouTube/Vimeo)
// are accepted for video without probing (they can't be loaded as a <video>).

import { MediaKind } from "./mediaSelectorConfig";

export interface MediaUrlValidation {
  ok: boolean;
  /** True for YouTube/Vimeo-style embeds (a player URL, not a direct file). */
  isEmbed?: boolean;
  reason?: string;
}

const PROBE_TIMEOUT_MS = 10000;

const isHttpUrl = (value: string): boolean => {
  try {
    const u = new URL(value);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

/** YouTube / Vimeo watch or share links — valid video embeds, not direct files. */
export const isEmbeddableVideoUrl = (value: string): boolean => {
  return /(?:youtube\.com\/watch|youtu\.be\/|youtube\.com\/embed\/|vimeo\.com\/)/i.test(value);
};

const probeImage = (url: string): Promise<boolean> =>
  new Promise((resolve) => {
    const img = new Image();
    const timer = setTimeout(() => {
      img.src = "";
      resolve(false);
    }, PROBE_TIMEOUT_MS);

    img.onload = () => {
      clearTimeout(timer);
      resolve(img.naturalWidth > 0);
    };
    img.onerror = () => {
      clearTimeout(timer);
      resolve(false);
    };
    img.src = url;
  });

const probeMediaElement = (url: string, kind: "video" | "audio"): Promise<boolean> =>
  new Promise((resolve) => {
    const el = document.createElement(kind);
    el.preload = "metadata";
    el.muted = true;

    const done = (ok: boolean) => {
      clearTimeout(timer);
      el.removeAttribute("src");
      el.load?.();
      resolve(ok);
    };

    const timer = setTimeout(() => done(false), PROBE_TIMEOUT_MS);

    el.onloadedmetadata = () => {
      // Videos must actually carry a picture; audio just needs a duration.
      if (kind === "video") {
        done((el as HTMLVideoElement).videoWidth > 0);
      } else {
        done(Number.isFinite(el.duration) && el.duration > 0);
      }
    };
    el.onerror = () => done(false);
    el.src = url;
  });

export const validateMediaUrl = async (rawUrl: string, kind: MediaKind): Promise<MediaUrlValidation> => {
  const url = (rawUrl || "").trim();

  if (!url) {
    return { ok: false, reason: "Enter a URL." };
  }

  if (!isHttpUrl(url)) {
    return { ok: false, reason: "Enter a full URL starting with http:// or https://." };
  }

  if (kind === "image") {
    const ok = await probeImage(url);
    return ok ? { ok: true } : { ok: false, reason: "That URL didn't load as an image." };
  }

  if (kind === "video") {
    if (isEmbeddableVideoUrl(url)) {
      return { ok: true, isEmbed: true };
    }

    const ok = await probeMediaElement(url, "video");
    return ok
      ? { ok: true }
      : { ok: false, reason: "That URL isn't a playable video (or a YouTube/Vimeo link)." };
  }

  const ok = await probeMediaElement(url, "audio");
  return ok ? { ok: true } : { ok: false, reason: "That URL didn't load as an audio file." };
};
